import type { Box, Point } from './markupShapes.ts';

const ANGLE_STEP = Math.PI / 4;

export function boxFromCorners(a: Point, b: Point): Box {
    return {
        x: Math.min(a.x, b.x),
        y: Math.min(a.y, b.y),
        width: Math.abs(b.x - a.x),
        height: Math.abs(b.y - a.y)
    };
}

export function boxOfPoints(points: readonly Point[]): Box {
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    return boxFromCorners(
        { x: Math.min(...xs), y: Math.min(...ys) },
        { x: Math.max(...xs), y: Math.max(...ys) }
    );
}

export function inflateBox(box: Box, by: number): Box {
    return {
        x: box.x - by,
        y: box.y - by,
        width: box.width + by * 2,
        height: box.height + by * 2
    };
}

export function boxContains(box: Box, point: Point): boolean {
    return (
        point.x >= box.x &&
        point.x <= box.x + box.width &&
        point.y >= box.y &&
        point.y <= box.y + box.height
    );
}

// Shift on a line or arrow: same length, direction rounded to the nearest 45°.
export function snapAngle(from: Point, to: Point): Point {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    if (length === 0) return to;
    const angle = Math.round(Math.atan2(dy, dx) / ANGLE_STEP) * ANGLE_STEP;
    return { x: from.x + Math.cos(angle) * length, y: from.y + Math.sin(angle) * length };
}

function directionOf(delta: number): number {
    return delta < 0 ? -1 : 1;
}

// Shift on a rectangle or ellipse: the longer side of the drag wins.
export function squareCorner(anchor: Point, corner: Point): Point {
    const dx = corner.x - anchor.x;
    const dy = corner.y - anchor.y;
    const side = Math.max(Math.abs(dx), Math.abs(dy));
    return { x: anchor.x + directionOf(dx) * side, y: anchor.y + directionOf(dy) * side };
}

// `ratio` is width / height of the box being resized.
export function keepAspectCorner(anchor: Point, corner: Point, ratio: number): Point {
    if (!(ratio > 0) || !Number.isFinite(ratio)) return corner;
    const dx = corner.x - anchor.x;
    const dy = corner.y - anchor.y;
    if (Math.abs(dx) >= Math.abs(dy) * ratio) {
        return { x: corner.x, y: anchor.y + (directionOf(dy) * Math.abs(dx)) / ratio };
    }
    return { x: anchor.x + directionOf(dx) * Math.abs(dy) * ratio, y: corner.y };
}
